/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useCache } from '../../providers/Cache.provider';

export type HubSpotDropdownValues = {
  platformUsages: vp.HubSpotDropdownData[];
  industries: vp.HubSpotDropdownData[];
  platformUsagesMappedValues: { [id: string]: HubSpotDropdownData };
  industriesMappedValues: { [id: string]: HubSpotDropdownData };
  platformUsagesDropDownOptions: { [key: string]: string };
  industriesDropDownOptions: { [key: string]: string };
};

export type HubSpotDropdownData = {
  label: string;
  value: string;
  displayOrder: number;
  hidden: boolean;
};

type HookProps = { api: vp.Api };
type HookTuple = [HubSpotDropdownValues, () => void];
type ApiHook = (props: HookProps) => HookTuple;

const useFetchHubSpotDropdownData: ApiHook = ({ api }) => {
  const source = axios.CancelToken.source();
  const { cache } = useCache();

  const [platformUsages, setPlatformUsages] = useState<vp.HubSpotDropdownData[]>([]);
  const [industries, setIndustries] = useState<vp.HubSpotDropdownData[]>([]);
  const [getHubSpotDropdownData, setGetHubSpotDropdownData] = useState(false);
  const [shouldUseCache, setShouldUseCache] = useState(false);

  const [getCachedPackages, cachePackages] = cache<{
    platformUsages?: vp.HubSpotDropdownData[];
    industries?: vp.HubSpotDropdownData[];
    platformUsagesMappedValues?: { [id: string]: HubSpotDropdownData };
    industriesMappedValues?: { [id: string]: HubSpotDropdownData };
  }>('hubSpotDropdownData');

  const cachedPackages = getCachedPackages();

  useEffect(() => {
    const loadData = async () => {
      if (shouldUseCache && cachedPackages) {
        return [setPlatformUsages(cachedPackages.platformUsages || []), setIndustries(cachedPackages.industries || [])];
      }

      const { fromObject } = vp.dataTransformers;
      const { PlatformUsages, Industries } = await api.fetchHubSpotDropdownData(source);

      if (PlatformUsages) {
        const platformUsagesList = fromObject(PlatformUsages).toArray();
        const platformUsagesMappedValues = buildMappedValues(platformUsagesList);

        cachePackages({ platformUsages: platformUsagesList, platformUsagesMappedValues });
        setPlatformUsages(platformUsagesList);
      }

      if (Industries) {
        const industriesList = fromObject(Industries).toArray();
        const industriesMappedValues = buildMappedValues(industriesList);

        cachePackages({ industries: industriesList, industriesMappedValues });
        setIndustries(industriesList);
      }

      return setGetHubSpotDropdownData(false);
    };

    if (getHubSpotDropdownData) void loadData();

    return () => {
      source.cancel();
    };
  }, [getHubSpotDropdownData]);

  return [
    {
      platformUsages,
      industries,
      platformUsagesMappedValues: cachedPackages?.platformUsagesMappedValues || {},
      industriesMappedValues: cachedPackages?.industriesMappedValues || {},
      platformUsagesDropDownOptions: buildDropDownOptions(platformUsages),
      industriesDropDownOptions: buildDropDownOptions(industries),
    },
    (options?: { useCached: boolean }) => {
      setGetHubSpotDropdownData(true);
      setShouldUseCache(options?.useCached || false);
    },
  ];

  function buildDropDownOptions(packages: vp.HubSpotDropdownData[]) {
    return packages.reduce((accumulator, current) => ({ ...accumulator, [current.value]: current.label }), {});
  }

  function buildMappedValues(packages: vp.HubSpotDropdownData[]) {
    return packages.reduce(
      (accumulator, current) => ({
        ...accumulator,
        [current.value]: {
          label: current.label,
          value: current.value,
          displayOrder: current.displayOrder,
          hidden: current.hidden,
        },
      }),
      {},
    );
  }
};

export default vopayApi.withApiHook<HookProps, HookTuple>(useFetchHubSpotDropdownData);
